import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { isRouteActive } from './sidebar-active';
import { useTranslation } from 'react-i18next';
import {
    Activity,
    Bell,
    CalendarClock,
    Cable,
    MonitorSmartphone,
    UsersRound,
    AtSign,
    BarChart3,
    CalendarDays,
    ChevronRight,
    CreditCard,
    Gauge,
    Globe,
    HeartPulse,
    Image as ImageIcon,
    ImagePlus,
    Bot,
    House,
    LayoutDashboard,
    LayoutGrid,
    LayoutList,
    LayoutTemplate,
    Link2,
    ListChecks,
    Mail,
    Megaphone,
    MessageCircle,
    MessageCircleQuestion,
    MessagesSquare,
    Package,
    PackageSearch,
    Palette,
    Plug,
    Plus,
    RefreshCw,
    Rocket,
    ScanSearch,
    Search,
    Settings2,
    ShoppingBag,
    Sofa,
    Sparkles,
    SquarePen,
    Store,
    Swords,
    SwatchBook,
    Target,
    Users,
    Wallet,
    Workflow,
    // feat/room-to-revenue
    Inbox,
    Sunrise,
    SquareKanban,
    TrendingUp,
    HardHat,
    QrCode,
    DoorOpen,
    PackageCheck,
    BotMessageSquare,
    // The Ardealy Network — Brand Portal
    Camera,
    Network,
    // The Ardealy Network (retailer side)
    Share2,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { ProTag } from '../../../components/design-system/plan/ProTag';
import { useWorkspaceStore } from './workspace.store';
import { WorkspaceId, effectiveWorkspace, workspaceById } from './workspaces';
import { AuthContext } from '../../../providers/AuthProvider';
import '../../brand/brand-i18n';
import { useSelector } from 'react-redux';
import { VendorState } from '../../../redux/vendor/rootReducer';
import AuthClientStore from '../../../common/services/auth-client.service';

/**
 * The vendor's navigation.
 *
 * One declarative tree rendered by shadcn's sidebar primitives. Everything
 * that used to be hand-styled — the active state, the collapse behaviour, the
 * icon-only mode — belongs to the component now; what stays here is what is
 * ours: which routes exist, what gates them, and which paths count as "this
 * item is the page you are on".
 */

interface NavItem {
  route: string;
  labelKey: string;
  /** Fallback if the key is missing from a language file. */
  label?: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Extra paths that should light this item up. */
  activeRoutes?: string[];
  /** Not on the vendor's plan. Still a link — the page is open to look at and says what
   *  Pro adds — marked with a small "Pro" tag. */
  locked?: boolean;
  /** Not rendered at all. For a row that has nothing behind it, as opposed to one the
   *  vendor could unlock by upgrading — that is `locked`. */
  hidden?: boolean;
  /** A word ("New") or a count, shown at the end of the row. */
  badge?: string | number;
  /** A locked child keeps its row and its link, and shows the Pro tag, like a locked item. */
  children?: Array<{ route: string; labelKey: string; locked?: boolean }>;
}

interface NavSection {
  /** The two groups every workspace starts with; they are drawn first, in this order. */
  id?: 'dashboard' | 'products';
  /** Omitted for the first group, which needs no heading. */
  labelKey?: string;
  /**
   * The workspace this group belongs to. Absent for the groups every
   * workspace needs — the dashboard, the catalogue, billing — which stay on
   * the rail whichever one is chosen. See workspaces.ts.
   */
  workspace?: WorkspaceId;
  /** A shared group that a given workspace does without. */
  hiddenIn?: WorkspaceId[];
  /** Not on a manufacturer's (brand) account. */
  brandHidden?: boolean;
  items: NavItem[];
}

/** Active when the path matches, or sits under one of the declared prefixes. */
function useIsActive() {
  const location = useLocation();

  /* The decision itself lives in sidebar-active.ts — three kinds of item match
     three different ways, and that is worth testing without a router. */
  return React.useCallback(
    (item: NavItem | { route: string; activeRoutes?: string[] }, amongTabs = false, defaultTab = false) =>
      isRouteActive({
        route: item.route,
        activeRoutes: (item as NavItem).activeRoutes,
        pathname: location.pathname,
        search: location.search,
        amongTabs,
        defaultTab,
      }),
    [location.pathname, location.search],
  );
}

export const VendorMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isActive = useIsActive();
  const { isFeatureLocked, isLimitDisabled, plan, planSlug } = useVendorPlan();
  /**
   * A vendor who has switched measurement off has nothing behind leads, analytics,
   * activity or email marketing — those screens are fed entirely by the usage events that
   * are no longer collected. Hidden rather than padlocked: a padlock means "upgrade to
   * unlock", and this is a deliberate choice they made, not a plan limit.
   *
   * Read from redux rather than the module-level config, which is a plain mutable export
   * that would not re-render the rail when the config lands after first paint.
   */
  const { analyticsEnabled } = useSelector((state: VendorState) => state.settings.theme);
  const measures = analyticsEnabled !== false;
  const chosen = useWorkspaceStore((s) => s.workspace);
  /*
   * A manufacturer's account (kind 'brand') has its own workspaces — see
   * workspacesFor. A choice remembered from elsewhere that it cannot use
   * falls back to the Brand Portal rather than drawing a retailer's rail.
   */
  const kind = (React.useContext(AuthContext)?.user as any)?.vendor?.kind as string | undefined;
  const isBrand = kind === 'brand';
  const workspace: WorkspaceId = effectiveWorkspace(chosen, kind);
  // A manufacturer's account (the Brand Portal) has no retailer network page.
  const isBrandAccount = isBrand;

  /*
   * Every workspace's rail has the same spine: its Dashboard first, then the
   * catalogue every workspace works with, then the workspace's own pages.
   *
   * Pages are rows, not folders. A parent that opened to reveal three children
   * hid the children until clicked and took a row for itself; now each group
   * is a heading and every page — and every tab worth going straight to — is
   * a row of its own. The rail shows one workspace at a time, so there is room.
   */
  // A custom plan is quoted per vendor and sits above every self-serve one.
  const onTopPlan = plan?.pricingType === 'custom' || planSlug === 'enterprise';

  const locked = {
    assistant: isFeatureLocked('shopperChat'),
    ads: isFeatureLocked('adsManager'),
    studio: isFeatureLocked('contentStudio'),
    email: isFeatureLocked('emailAutomation'),
    social: isFeatureLocked('socialScheduler'),
    integration: isFeatureLocked('apiIntegration'),
  };

  // Declared in full, then filtered, so each row's own condition sits with the row.
  const sections: NavSection[] = [
    {
      // The one row every workspace has, pointing at that workspace's own dashboard.
      id: 'dashboard',
      labelKey: 'SIDEBAR.DASHBOARD.TITLE',
      items: [
        {
          route: workspaceById(workspace).home,
          labelKey: 'SIDEBAR.DASHBOARD.TITLE',
          icon: LayoutDashboard,
          // Home's front door is not a dashboard; it leads the team group below.
          // The Brand Portal's overview is the first row of its own group.
          hidden: workspace === 'home' || workspace === 'brand',
          locked: workspace === 'assistant' ? locked.assistant : workspace === 'ads' ? locked.ads : false,
        },
      ],
    },
    {
      /*
       * Home's own front: Home first, then the team — every conversation with it, who is on it and what each has
       * done, and the classic overview of the numbers for whoever still wants
       * the charts.
       */
      labelKey: 'AGENTS.NAV.SECTION',
      workspace: 'home',
      // A brand's Home is its account; the AI team works on a shop's own site and leads.
      brandHidden: true,
      items: [
        { route: '/home/dashboard', labelKey: 'AGENTS.NAV.HOME', label: 'Home', icon: House },
        // feat/room-to-revenue: everything waiting for a yes, in one list.
        { route: '/home/decisions', labelKey: 'R2R.NAV.DECISIONS', label: 'Decisions', icon: Inbox, badge: t('R2R.NAV.NEW') as string },
        { route: '/home/chat', labelKey: 'AGENTS.NAV.CONVERSATIONS', label: 'Conversations', icon: MessagesSquare },
        { route: '/home/agents', labelKey: 'AGENTS.NAV.AGENTS', label: 'AI team', icon: Bot },
        // Ardi outside the chat box: where it can be reached, and what it does on a schedule.
        { route: '/home/channels', labelKey: 'ARDI.NAV.CHANNELS', label: 'Channels', icon: Cable, badge: t('ARDI.NAV.NEW') as string },
        { route: '/home/routines', labelKey: 'ARDI.NAV.ROUTINES', label: 'Routines', icon: CalendarClock },
        { route: '/home/brief', labelKey: 'R2R.NAV.BRIEF', label: 'Morning brief', icon: Sunrise },
        { route: '/home/overview', labelKey: 'AGENTS.NAV.OVERVIEW', label: 'Overview', icon: LayoutGrid },
      ],
    },
    {
      id: 'products',
      labelKey: 'SIDEBAR.PRODUCTS.TITLE',
      // A brand's own catalogue lives in the network (Brand › Demand); the shop catalogue
      // stays on its Marketing rail, where Content Studio picks products from it.
      hiddenIn: ['brand'],
      items: [
        { route: '/products/catalog', labelKey: 'SIDEBAR.PRODUCTS.CATALOG', icon: Package },
        { route: '/products/new', labelKey: 'SIDEBAR.PRODUCTS.NEW_PRODUCT', icon: Plus },
        // The Ardealy Network: products of this catalogue already digitized by their brand, and brands to add from.
        // Ardealy support only (an Act-as session) until the network has brands to offer shops.
        { route: '/products/network', labelKey: 'NETWORK_RETAILER.NAV', label: 'Network', icon: Share2, hidden: isBrandAccount || !AuthClientStore.isSupportSession() },
        /* Categories, import and import history open from the catalogue itself
           (and the command palette), so the rail keeps only the way in. */
        { route: '/products/sync', labelKey: 'SIDEBAR.CUSTOMIZATION.SYNCHRONIZATION', icon: RefreshCw, locked: locked.integration },
      ],
    },

    // ── Brand Portal (the Ardealy Network) ──────────────────────────────
    {
      labelKey: 'BRAND_PORTAL.NAV.SECTION_DEMAND',
      workspace: 'brand',
      items: [
        { route: '/brand/overview', labelKey: 'BRAND_PORTAL.NAV.OVERVIEW', label: 'Overview', icon: LayoutDashboard },
        { route: '/brand/demand', labelKey: 'BRAND_PORTAL.NAV.DEMAND', label: 'Demand', icon: BarChart3, activeRoutes: ['/brand/demand', '/brand/products'] },
        { route: '/brand/unmet', labelKey: 'BRAND_PORTAL.NAV.UNMET', label: 'Unmet demand', icon: Camera },
      ],
    },
    {
      labelKey: 'BRAND_PORTAL.NAV.SECTION_RETAILERS',
      workspace: 'brand',
      items: [
        { route: '/brand/distribution', labelKey: 'BRAND_PORTAL.NAV.DISTRIBUTION', label: 'Distribution', icon: Network },
        { route: '/brand/announcements', labelKey: 'BRAND_PORTAL.NAV.ANNOUNCEMENTS', label: 'Announcements', icon: Megaphone },
      ],
    },

    // ── Home ────────────────────────────────────────────────────────────
    {
      labelKey: 'SIDEBAR.HOME.SETUP',
      workspace: 'home',
      // The website connection is the shop's visualizer; a brand has none.
      brandHidden: true,
      items: [
        { route: '/home/integration', labelKey: 'SIDEBAR.HOME.WEBSITE', label: 'Website', icon: Globe },
      ],
    },
    {
      labelKey: 'SIDEBAR.ACCOUNT.TITLE',
      workspace: 'home',
      items: [
        { route: '/home/billing/plan', labelKey: 'SIDEBAR.PLAN_BILLING', icon: CreditCard },
        // Nothing to upgrade to from a custom (Enterprise) plan.
        { route: '/home/billing/upgrade', labelKey: 'SIDEBAR.UPGRADE', icon: Rocket, hidden: onTopPlan },
        { route: '/home/team', labelKey: 'VENDOR_TEAM.NAV', label: 'Team', icon: UsersRound },
        { route: '/home/devices', labelKey: 'SEATS.NAV', label: 'Devices', icon: MonitorSmartphone },
        { route: '/home/notifications', labelKey: 'ARDI.NAV.NOTIFICATIONS', label: 'Notifications', icon: Bell },
      ],
    },

    // ── Sales (feat/room-to-revenue) ────────────────────────────────────
    {
      labelKey: 'R2R.NAV.SALES_PIPELINE',
      workspace: 'sales',
      items: [
        { route: '/sales/desk', labelKey: 'R2R.NAV.DESK', label: 'Lead desk', icon: SquareKanban, activeRoutes: ['/sales/leads'] },
        { route: '/sales/requests', labelKey: 'R2R.NAV.REQUESTS', label: 'Requests', icon: Inbox },
        { route: '/sales/influence', labelKey: 'R2R.NAV.INFLUENCE', label: 'Sales influenced', icon: TrendingUp },
      ],
    },
    {
      labelKey: 'R2R.NAV.SALES_GROW',
      workspace: 'sales',
      items: [
        { route: '/sales/trade', labelKey: 'R2R.NAV.TRADE', label: 'Trade prospects', icon: HardHat },
        { route: '/sales/showroom', labelKey: 'R2R.NAV.SHOWROOM', label: 'Showroom QR codes', icon: QrCode },
        { route: '/sales/rooms', labelKey: 'R2R.NAV.ROOMS', label: 'Saved rooms', icon: DoorOpen },
      ],
    },

    // ── Visualizer ──────────────────────────────────────────────────────
    {
      /*
       * Who came (leads), what they did in aggregate (analytics), and what
       * happened when (activity) — all readings of the usage events, so with
       * measurement off there is nothing behind any of them.
       */
      labelKey: 'SIDEBAR.ENGAGEMENT.TITLE',
      workspace: 'visualizer',
      items: [
        { route: '/visualizer/engagement/leads', labelKey: 'SIDEBAR.ENGAGEMENT.LEADS', icon: Users, locked: isLimitDisabled('maxLeads'), hidden: !measures },
        { route: '/visualizer/engagement/analytics?tab=overview', labelKey: 'SIDEBAR.ENGAGEMENT.ANALYTICS', icon: BarChart3, locked: isFeatureLocked('advancedAnalytics'), hidden: !measures },
        { route: '/visualizer/engagement/analytics?tab=products', labelKey: 'SIDEBAR.ENGAGEMENT.PRODUCT_PERFORMANCE', label: 'Product performance', icon: PackageSearch, locked: isFeatureLocked('advancedAnalytics'), hidden: !measures },
        { route: '/visualizer/engagement/activity', labelKey: 'SIDEBAR.VISUALIZER.ACTIVITY', icon: Activity, hidden: !measures },
      ],
    },
    {
      labelKey: 'SIDEBAR.VISUALIZER.PARAMETERS',
      workspace: 'visualizer',
      items: [
        { route: '/visualizer/settings?tab=general', labelKey: 'VISUALIZER_STUDIO.TITLE.BRAND', label: 'Brand', icon: Palette },
        { route: '/visualizer/settings?tab=shopping', labelKey: 'VISUALIZER_STUDIO.TITLE.SHOPPING', label: 'Shopping', icon: ShoppingBag },
        { route: '/visualizer/settings?tab=advanced', labelKey: 'SIDEBAR.CUSTOMIZATION.ADVANCED', icon: Settings2 },
        { route: '/visualizer/settings?tab=product-display', labelKey: 'SIDEBAR.CUSTOMIZATION.PRODUCT_DISPLAY', icon: LayoutTemplate },
        {
          route: '/visualizer/settings?tab=features',
          labelKey: 'VISUALIZER_STUDIO.GROUP.SITE',
          label: 'On your site',
          icon: Plug,
          locked: locked.integration,
          activeRoutes: ['/visualizer/settings?tab=matching', '/visualizer/settings?tab=button'],
        },
        { route: '/visualizer/spaces', labelKey: 'SIDEBAR.VISUALIZER.MY_SELECTION', icon: Sofa },
      ],
    },

    // ── Shopping Assistant ──────────────────────────────────────────────
    {
      labelKey: 'SIDEBAR.SHOPPING_ASSISTANT.SHOPPERS',
      workspace: 'assistant',
      items: [
        { route: '/assistant/conversations', labelKey: 'SIDEBAR.SHOPPING_ASSISTANT.CONVERSATIONS', icon: MessagesSquare, locked: locked.assistant },
        { route: '/assistant/questions', labelKey: 'SIDEBAR.SHOPPING_ASSISTANT.QUESTIONS', icon: MessageCircleQuestion, locked: locked.assistant },
      ],
    },
    {
      labelKey: 'SIDEBAR.SHOPPING_ASSISTANT.SETTINGS',
      workspace: 'assistant',
      items: [
        { route: '/assistant/settings?tab=general', labelKey: 'SIDEBAR.SHOPPING_ASSISTANT.TAB_GENERAL', label: 'Launcher & greeting', icon: MessageCircle, locked: locked.assistant },
        { route: '/assistant/settings?tab=appearance', labelKey: 'SIDEBAR.SHOPPING_ASSISTANT.TAB_APPEARANCE', label: 'Appearance', icon: Palette, locked: locked.assistant },
        { route: '/assistant/settings?tab=questions', labelKey: 'SIDEBAR.SHOPPING_ASSISTANT.TAB_QUESTIONS', label: 'Page questions', icon: ListChecks, locked: locked.assistant },
        { route: '/assistant/settings?tab=photos', labelKey: 'SIDEBAR.SHOPPING_ASSISTANT.TAB_PHOTOS', label: 'Room photos', icon: ImagePlus, locked: locked.assistant },
      ],
    },

    // ── Marketing ───────────────────────────────────────────────────────
    {
      labelKey: 'SIDEBAR.MARKETING.CONTENT',
      workspace: 'marketing',
      items: [
        { route: '/marketing/brand', labelKey: 'SIDEBAR.MARKETING.BRAND', icon: SwatchBook, locked: locked.studio },
        { route: '/marketing/content-studio', labelKey: 'SIDEBAR.MARKETING.CONTENT_STUDIO', icon: ImageIcon, locked: locked.studio },
        { route: '/marketing/content-packs', labelKey: 'R2R.NAV.CONTENT_PACKS', label: 'Content packs', icon: PackageCheck, locked: locked.studio },
      ],
    },
    {
      // Email marketing writes to leads, and no leads are captured without measurement.
      labelKey: 'SIDEBAR.MARKETING.EMAIL_MARKETING',
      workspace: 'marketing',
      items: [
        { route: '/marketing/email/compose', labelKey: 'SIDEBAR.MARKETING.COMPOSE_EMAIL', icon: SquarePen, locked: locked.email, hidden: !measures },
        { route: '/marketing/email/automation', labelKey: 'SIDEBAR.MARKETING.EMAIL_AUTOMATION', icon: Workflow, locked: locked.email, hidden: !measures },
        { route: '/marketing/email/settings?tab=templates', labelKey: 'SIDEBAR.MARKETING.EMAIL_SETTINGS', icon: Mail, locked: locked.email, hidden: !measures, activeRoutes: ['/marketing/email/settings'] },
      ],
    },
    {
      labelKey: 'SIDEBAR.MARKETING.PLANNER',
      workspace: 'marketing',
      items: [
        { route: '/marketing/social-scheduler?tab=calendar', labelKey: 'SIDEBAR.MARKETING.SS_CALENDAR', icon: CalendarDays, locked: locked.social },
        { route: '/marketing/social-scheduler?tab=posts', labelKey: 'SIDEBAR.MARKETING.SS_POSTS', icon: LayoutList, locked: locked.social },
        { route: '/marketing/social-scheduler?tab=accounts', labelKey: 'SIDEBAR.MARKETING.SS_ACCOUNTS', icon: AtSign, locked: locked.social },
      ],
    },

    // ── Ads ─────────────────────────────────────────────────────────────
    {
      labelKey: 'SIDEBAR.ADS.PERFORMANCE',
      workspace: 'ads',
      items: [
        { route: '/ads/campaigns', labelKey: 'SIDEBAR.ADS.CAMPAIGNS', icon: Megaphone, locked: locked.ads },
        { route: '/ads/suggestions', labelKey: 'SIDEBAR.ADS.SUGGESTIONS', icon: Sparkles, locked: locked.ads },
      ],
    },
    {
      labelKey: 'SIDEBAR.ADS.PLANNING',
      workspace: 'ads',
      items: [
        { route: '/ads/audiences', labelKey: 'SIDEBAR.ADS.AUDIENCES', icon: Target, locked: locked.ads },
        { route: '/ads/budgets', labelKey: 'SIDEBAR.ADS.BUDGETS', icon: Gauge, locked: locked.ads },
        { route: '/ads/connections', labelKey: 'SIDEBAR.ADS.CONNECTIONS', icon: Plug, locked: locked.ads },
      ],
    },

    // ── Business ────────────────────────────────────────────────────────
    {
      labelKey: 'SIDEBAR.BUSINESS.VISIBILITY',
      workspace: 'business',
      items: [
        { route: '/business/visibility/search', labelKey: 'SIDEBAR.MARKET.SEARCH', icon: Search },
        { route: '/business/visibility/profile', labelKey: 'SIDEBAR.MARKET.PROFILE', icon: Store },
        { route: '/business/visibility/backlinks', labelKey: 'SIDEBAR.MARKET.BACKLINKS', icon: Link2 },
        { route: '/business/visibility/health', labelKey: 'SIDEBAR.MARKET.HEALTH', icon: HeartPulse },
        { route: '/business/ai', labelKey: 'R2R.NAV.AI', label: 'AI assistants', icon: BotMessageSquare },
      ],
    },
    {
      labelKey: 'SIDEBAR.BUSINESS.COMPETITORS',
      workspace: 'business',
      items: [
        { route: '/business/competitors/rivals', labelKey: 'SIDEBAR.MARKET.COMPETITORS', icon: Swords },
        { route: '/business/competitors/gaps', labelKey: 'SIDEBAR.MARKET.GAPS', icon: ScanSearch },
      ],
    },
    {
      labelKey: 'SIDEBAR.BUSINESS.SALES',
      workspace: 'business',
      items: [
        { route: '/business/sales/revenue', labelKey: 'SIDEBAR.SALES.REVENUE', icon: Wallet },
        { route: '/business/sales/shop', labelKey: 'SIDEBAR.SALES.SHOP', icon: ShoppingBag },
      ],
    },
  ];

  const label = (item: { labelKey: string; label?: string }) =>
    (item.label ? t(item.labelKey, item.label) : t(item.labelKey)) as string;

  const renderItem = (item: NavItem, siblings: NavItem[]) => {
    /*
     * Rows that are tabs of one page — Calendar, Posts, Accounts — light up as
     * tabs: only the one in the URL, and the first when the URL names none.
     */
    const base = item.route.split('?')[0];
    const tabs = siblings.filter((s) => s.route.includes('?tab=') && s.route.split('?')[0] === base);
    const active = item.route.includes('?tab=') && tabs.length > 1
      ? isActive(item, true, tabs[0] === item)
      : isActive(item);
    const Icon = item.icon;

    const button = (
      <SidebarMenuButton
        isActive={active}
        tooltip={label(item)}
        // Every row navigates, locked or not: a feature the plan lacks is
        // still a page to look at, and the page is where the unlock is said.
        onClick={() => navigate(item.route)}
        data-locked={item.locked || undefined}
        className={cn(
          // Grey at rest, black on hover or when current — word and glyph
          // together, since the icon inherits the row's colour.
          'h-8 gap-2.5 text-sm font-normal',
          'data-[active=true]:font-medium',
        )}
      >
        <Icon className="size-4" />
        <span>{label(item)}</span>
        {item.locked ? (
          <ProTag className="ms-auto group-data-[collapsible=icon]:hidden" />
        ) : item.badge !== undefined ? (
          <span
            className={cn(
              'ms-auto text-[11px] leading-none',
              typeof item.badge === 'string'
                ? 'rounded-full border border-success/35 bg-success/10 px-2 py-[3px] font-medium text-success'
                : 'text-muted-foreground',
            )}
          >
            {item.badge}
          </span>
        ) : null}
      </SidebarMenuButton>
    );

    if (!item.children?.length) {
      return <SidebarMenuItem key={item.route}>{button}</SidebarMenuItem>;
    }

    return (
      <Collapsible key={item.route} asChild defaultOpen={active} className="group/collapsible">
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton
              isActive={active}
              tooltip={label(item)}
              data-locked={item.locked || undefined}
              className={cn(
                'h-8 gap-2.5 text-sm font-normal',
                'data-[active=true]:font-medium',
              )}
            >
              <Icon className="size-4" />
              <span>{label(item)}</span>
              <ChevronRight className="ms-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {/* Whether these children are tabs of one page, so the
                  tab-less one does not light up beside the others. Derived
                  from the group rather than flagged per item, so a fourth tab
                  cannot be added without it. */}
              {item.children.map((child, childIndex) => (
                <SidebarMenuSubItem key={child.route}>
                  <SidebarMenuSubButton
                    isActive={isActive(child, item.children!.some((c) => c.route.includes('?tab=')), childIndex === 0)}
                    onClick={() => navigate(child.route)}
                    data-locked={child.locked || undefined}
                    className="cursor-pointer text-sm"
                  >
                    <span>{t(child.labelKey)}</span>
                    {child.locked && <ProTag className="ms-auto" />}
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  };

  // Drop hidden rows, then any section left with nothing in it — a heading above an empty
  // group reads as a broken menu rather than a deliberately shorter one.
  const visibleSections = sections
    // The chosen workspace's groups, and the shared ones.
    .filter((section) => !section.workspace || section.workspace === workspace)
    .filter((section) => !section.hiddenIn?.includes(workspace) && !(isBrand && section.brandHidden))
    .map((section) => ({ ...section, items: section.items.filter((item) => !item.hidden) }))
    .filter((section) => section.items.length > 0);

  return (
    <>
      {visibleSections.map((section, index) => (
        <SidebarGroup
          key={section.labelKey ?? `group-${index}`}
          className={cn('py-0', index === 0 ? 'pt-0' : 'pt-5')}
        >
          {section.labelKey && (
            <SidebarGroupLabel className="h-6 px-2 text-xs leading-4 font-normal tracking-normal">
              {t(section.labelKey)}
            </SidebarGroupLabel>
          )}
          <SidebarMenu className="gap-0">{section.items.map((item) => renderItem(item, section.items))}</SidebarMenu>
        </SidebarGroup>
      ))}
    </>
  );
};
